import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tabs,
  Tab,
  Tooltip,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  Refresh,
  FileCopy,
  LocalPharmacy,
  Store,
  Warning,
  Add,
  Delete,
  LocalShipping,
  LocalOffer,
  AutorenewRounded as EnrichIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  LocationCity,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import * as pharmaApi from "../../services/pharmaApi";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  formGroup: {
    marginBottom: theme.spacing(2),
  },
  switchLabel: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: 0,
    width: "100%",
  },
  tokenField: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.background.default,
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
  },
  credentialsBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  credentialRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  credentialLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    minWidth: 150,
  },
  credentialValue: {
    fontFamily: "monospace",
    flex: 1,
    marginLeft: theme.spacing(2),
    wordBreak: "break-all",
  },
  copyButton: {
    marginLeft: theme.spacing(1),
  },
  warningText: {
    color: theme.palette.warning.main,
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
  },
  storesSection: {
    marginTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
  },
  storeItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  storeInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  storeName: {
    fontWeight: 600,
  },
  storeId: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  noStoresWarning: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "#3a3000" : "#fff8e1",
    borderRadius: theme.spacing(1),
    color: theme.palette.warning.dark,
  },
  feeListItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  addItemButton: {
    marginTop: theme.spacing(1),
  },
  categoryItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionSubtitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  // Novos estilos para CRUD de localizações
  locationTable: {
    marginTop: theme.spacing(2),
    "& th": {
      fontWeight: 600,
      backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
    },
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  locationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  chipActive: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  chipInactive: {
    backgroundColor: theme.palette.grey[400],
  },
  groupLevel: {
    paddingLeft: (props) => props?.level ? theme.spacing(props.level * 2) : 0,
  },
  treeItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const PharmaSettings = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCosmosToken, setSavingCosmosToken] = useState(false);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showCosmosToken, setShowCosmosToken] = useState(false);

  // Estados para Cidades, Bairros e Grupos
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationTab, setLocationTab] = useState(0);

  // Dialogs de CRUD
  const [cityDialog, setCityDialog] = useState({ open: false, data: null, mode: "create" });
  const [neighborhoodDialog, setNeighborhoodDialog] = useState({ open: false, data: null, mode: "create" });
  const [groupDialog, setGroupDialog] = useState({ open: false, data: null, mode: "create" });

  // Ref para rastrear o valor inicial do token Cosmos (evitar salvar na carga inicial)
  const cosmosTokenInitialRef = useRef(null);
  const cosmosTokenSaveTimeoutRef = useRef(null);

  const [settings, setSettings] = useState({
    showSameFormulaProducts: true,
    suggestRelatedProducts: true,
    enableKeyboardShortcuts: true,
    enableQuotes: false,
    quoteValidityDays: 7,
    defaultDeliveryTimeMinutes: 60,
    saleFarewellMessage: "Obrigado pela preferência! Volte sempre.",
    saleConfirmationMessage: "",
    quoteMessage: "",
    deliveryMessage: "",
    sendSaleConfirmation: true,
    sendQuoteMessage: true,
    apiEnabled: true,
    hasApiToken: false,
    // Credenciais do Módulo OtimizAPP API Pharma (geradas automaticamente)
    clientId: "",
    clientSecret: "",
    ecommerceUser: "",
    ecommercePassword: "",
    apiUrl: "",
    apiToken: "",
    // Lojas cadastradas (cada uma tem seu erpStoreId)
    stores: [],
    // Configurações de Produtos
    sendProductPromotions: false,
    sendPromotionsAndFullMix: false,
    allowControlledProducts: false,
    sumAllStores: false,
    // Taxas de Entrega
    deliveryFeeByNeighborhood: [],
    deliveryFeeByCity: [],
    deliveryFeePerKm: 0,
    deliveryBaseFee: 0,
    deliveryFreeAbove: null,
    // Descontos por Categoria
    categoryDiscountDefaults: [],
    defaultMinDiscount: 0,
    defaultMaxDiscount: 15,
    // Filtros Padrão do Painel de Vendas
    salesPanelDefaultActiveFilter: "true",
    salesPanelDefaultStockFilter: "with",
    // Enriquecimento de Dados (Bluesoft Cosmos)
    cosmosApiToken: "",
  });

  useEffect(() => {
    loadSettings();
    loadLocations();
  }, []);

  // Carregar cidades, bairros e grupos
  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      const [citiesRes, neighborhoodsRes, groupsRes] = await Promise.all([
        pharmaApi.listCities({ limit: 500 }),
        pharmaApi.listNeighborhoods({ limit: 500 }),
        pharmaApi.getProductGroupTree(),
      ]);
      setCities(citiesRes.data || []);
      setNeighborhoods(neighborhoodsRes.data || []);
      setProductGroups(groupsRes || []);
    } catch (err) {
      console.error("Erro ao carregar localizações:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  // CRUD Cidades
  const handleSaveCity = async (cityData) => {
    try {
      if (cityDialog.mode === "edit" && cityDialog.data?.id) {
        await pharmaApi.updateCity(cityDialog.data.id, cityData);
        toast.success("Cidade atualizada!");
      } else {
        await pharmaApi.createCity(cityData);
        toast.success("Cidade criada!");
      }
      setCityDialog({ open: false, data: null, mode: "create" });
      loadLocations();
    } catch (err) {
      toast.error("Erro ao salvar cidade");
      console.error(err);
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm("Excluir esta cidade?")) return;
    try {
      await pharmaApi.deleteCity(cityId);
      toast.success("Cidade removida!");
      loadLocations();
    } catch (err) {
      toast.error("Erro ao excluir cidade");
    }
  };

  // CRUD Bairros
  const handleSaveNeighborhood = async (neighborhoodData) => {
    try {
      if (neighborhoodDialog.mode === "edit" && neighborhoodDialog.data?.id) {
        await pharmaApi.updateNeighborhood(neighborhoodDialog.data.id, neighborhoodData);
        toast.success("Bairro atualizado!");
      } else {
        await pharmaApi.createNeighborhood(neighborhoodData);
        toast.success("Bairro criado!");
      }
      setNeighborhoodDialog({ open: false, data: null, mode: "create" });
      loadLocations();
    } catch (err) {
      toast.error("Erro ao salvar bairro");
      console.error(err);
    }
  };

  const handleDeleteNeighborhood = async (neighborhoodId) => {
    if (!window.confirm("Excluir este bairro?")) return;
    try {
      await pharmaApi.deleteNeighborhood(neighborhoodId);
      toast.success("Bairro removido!");
      loadLocations();
    } catch (err) {
      toast.error("Erro ao excluir bairro");
    }
  };

  // CRUD Grupos de Produtos
  const handleSaveGroup = async (groupData) => {
    try {
      if (groupDialog.mode === "edit" && groupDialog.data?.id) {
        await pharmaApi.updateProductGroup(groupDialog.data.id, groupData);
        toast.success("Grupo atualizado!");
      } else {
        await pharmaApi.createProductGroup(groupData);
        toast.success("Grupo criado!");
      }
      setGroupDialog({ open: false, data: null, mode: "create" });
      loadLocations();
    } catch (err) {
      toast.error("Erro ao salvar grupo");
      console.error(err);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Excluir este grupo?")) return;
    try {
      await pharmaApi.deleteProductGroup(groupId);
      toast.success("Grupo removido!");
      loadLocations();
    } catch (err) {
      toast.error("Erro ao excluir grupo");
    }
  };

  // Flatten group tree for select options
  const flattenGroups = (groups, level = 0) => {
    let result = [];
    for (const group of groups) {
      result.push({ ...group, level });
      if (group.children?.length > 0) {
        result = result.concat(flattenGroups(group.children, level + 1));
      }
    }
    return result;
  };

  // Auto-save do token Cosmos com debounce (1.5 segundos após parar de digitar)
  useEffect(() => {
    // Não fazer nada se ainda estiver carregando ou se o ref inicial for null (primeira carga)
    if (loading || cosmosTokenInitialRef.current === null) {
      return;
    }

    const currentToken = settings.cosmosApiToken || "";
    const initialToken = cosmosTokenInitialRef.current || "";

    // Só salvar se o valor mudou em relação ao inicial
    if (currentToken === initialToken) {
      return;
    }

    // Limpar timeout anterior se existir
    if (cosmosTokenSaveTimeoutRef.current) {
      clearTimeout(cosmosTokenSaveTimeoutRef.current);
    }

    // Configurar novo timeout para salvar
    cosmosTokenSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setSavingCosmosToken(true);
        await pharmaApi.updateSettings({ cosmosApiToken: currentToken });
        // Atualizar o ref para o novo valor salvo
        cosmosTokenInitialRef.current = currentToken;
        toast.success("Token Cosmos salvo automaticamente!");
      } catch (err) {
        console.error("Erro ao salvar token Cosmos:", err);
        toast.error("Erro ao salvar token Cosmos");
      } finally {
        setSavingCosmosToken(false);
      }
    }, 1500); // 1.5 segundos de debounce

    // Cleanup: limpar timeout ao desmontar ou quando o valor mudar
    return () => {
      if (cosmosTokenSaveTimeoutRef.current) {
        clearTimeout(cosmosTokenSaveTimeoutRef.current);
      }
    };
  }, [settings.cosmosApiToken, loading]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Use PharmaFlow API
      const data = await pharmaApi.getSettings();
      setSettings(data);
      // Guardar o valor inicial do token Cosmos para evitar salvar na carga
      cosmosTokenInitialRef.current = data.cosmosApiToken || "";
    } catch (err) {
      toast.error("Erro ao carregar configurações");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Helper to convert string to number, with fallback
      const toNumber = (val, fallback = 0) => {
        if (val === "" || val === null || val === undefined) return fallback;
        const num = parseFloat(val);
        return isNaN(num) ? fallback : num;
      };

      // Helper to convert to int with min/max bounds
      const toInt = (val, min, max, fallback) => {
        const num = toNumber(val, fallback);
        return Math.max(min, Math.min(max, Math.round(num)));
      };

      // Only send fields that are allowed by UpdateTenantSettingsDto
      // (excludes read-only fields like id, tenantId, hasCredentials, clientId, stores, etc.)
      const settingsToSave = {
        showSameFormulaProducts: settings.showSameFormulaProducts,
        suggestRelatedProducts: settings.suggestRelatedProducts,
        showCustomerSuggestions: settings.showCustomerSuggestions,
        offerSuggestionsBeforeFinalize: settings.offerSuggestionsBeforeFinalize,
        enableKeyboardShortcuts: settings.enableKeyboardShortcuts,
        enableQuotes: settings.enableQuotes,
        defaultDeliveryTimeMinutes: toInt(settings.defaultDeliveryTimeMinutes, 1, 1440, 60),
        quoteValidityDays: toInt(settings.quoteValidityDays, 1, 30, 7),
        productsDefaultActiveFilter: settings.productsDefaultActiveFilter,
        productsDefaultStockFilter: settings.productsDefaultStockFilter,
        salesPanelDefaultActiveFilter: settings.salesPanelDefaultActiveFilter,
        salesPanelDefaultStockFilter: settings.salesPanelDefaultStockFilter,
        saleFarewellMessage: settings.saleFarewellMessage,
        saleConfirmationMessage: settings.saleConfirmationMessage,
        quoteMessage: settings.quoteMessage,
        deliveryMessage: settings.deliveryMessage,
        sendSaleConfirmation: settings.sendSaleConfirmation,
        sendQuoteMessage: settings.sendQuoteMessage,
        apiEnabled: settings.apiEnabled,
        sendProductPromotions: settings.sendProductPromotions,
        sendPromotionsAndFullMix: settings.sendPromotionsAndFullMix,
        allowControlledProducts: settings.allowControlledProducts,
        sumAllStores: settings.sumAllStores,
        deliveryFeePerKm: toNumber(settings.deliveryFeePerKm, 0),
        deliveryBaseFee: toNumber(settings.deliveryBaseFee, 0),
        deliveryFreeAbove: settings.deliveryFreeAbove === "" || settings.deliveryFreeAbove === null ? null : toNumber(settings.deliveryFreeAbove, null),
        defaultMinDiscount: toNumber(settings.defaultMinDiscount, 0),
        defaultMaxDiscount: toNumber(settings.defaultMaxDiscount, 15),
        deliveryFeeByNeighborhood: settings.deliveryFeeByNeighborhood,
        deliveryFeeByCity: settings.deliveryFeeByCity,
        categoryDiscountDefaults: settings.categoryDiscountDefaults,
        cosmosApiToken: settings.cosmosApiToken,
      };
      // Use PharmaFlow API
      const data = await pharmaApi.updateSettings(settingsToSave);
      setSettings(prev => ({ ...prev, ...(data.settings || data) }));
      toast.success("Configurações salvas com sucesso!");
    } catch (err) {
      toast.error("Erro ao salvar configurações");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCredentials = async () => {
    if (settings.hasApiToken) {
      if (!window.confirm(
        "Ao gerar novas credenciais, as credenciais anteriores serão invalidadas. " +
        "Todas as integrações que usam as credenciais atuais deixarão de funcionar. " +
        "Deseja continuar?"
      )) {
        return;
      }
    }

    try {
      setGeneratingCredentials(true);
      // Use PharmaFlow API for credential generation
      const data = await pharmaApi.generateCredentials();
      setSettings(prev => ({
        ...prev,
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        ecommerceUser: data.ecommerceUser,
        ecommercePassword: data.ecommercePassword,
        apiUrl: data.apiUrl,
        apiToken: data.apiToken,
        stores: data.stores || prev.stores,
        hasCredentials: true,
        hasApiToken: true,
      }));
      setShowCredentials(true);
      toast.success("Credenciais geradas com sucesso! Copie e guarde em local seguro.");
    } catch (err) {
      toast.error("Erro ao gerar credenciais");
      console.error(err);
    } finally {
      setGeneratingCredentials(false);
    }
  };

  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado para a área de transferência`);
  };

  const handleCopyAllCredentials = () => {
    const storesText = settings.stores && settings.stores.length > 0
      ? settings.stores.map(s => `  - ${s.name}: ${s.erpStoreId}${s.cnpj ? ` (CNPJ: ${s.cnpj})` : ""}`).join("\n")
      : "  (Nenhuma loja cadastrada)";

    const credentials = `
=== CREDENCIAIS API PHARMA ===

Client ID: ${settings.clientId}
Client Secret: ${settings.clientSecret}
Usuário Ecommerce: ${settings.ecommerceUser}
Senha Ecommerce: ${settings.ecommercePassword}
URL API: ${settings.apiUrl}
Token de Acesso: ${settings.apiToken}

=== LOJAS CADASTRADAS ===
${storesText}

==============================
    `.trim();
    navigator.clipboard.writeText(credentials);
    toast.success("Todas as credenciais copiadas para a área de transferência");
  };

  const handleChange = (field) => (event) => {
    let value;
    if (event.target.type === "checkbox") {
      value = event.target.checked;
    } else if (event.target.type === "number") {
      // For number fields, keep as string while editing to allow clearing
      // The value will be converted to number on save
      value = event.target.value;
    } else {
      value = event.target.value;
    }
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Handlers for delivery fee by neighborhood
  const handleAddNeighborhoodFee = () => {
    setSettings(prev => ({
      ...prev,
      deliveryFeeByNeighborhood: [...(prev.deliveryFeeByNeighborhood || []), { neighborhood: "", fee: 0 }]
    }));
  };

  const handleUpdateNeighborhoodFee = (index, field, value) => {
    setSettings(prev => {
      const updated = [...(prev.deliveryFeeByNeighborhood || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, deliveryFeeByNeighborhood: updated };
    });
  };

  const handleRemoveNeighborhoodFee = (index) => {
    setSettings(prev => ({
      ...prev,
      deliveryFeeByNeighborhood: (prev.deliveryFeeByNeighborhood || []).filter((_, i) => i !== index)
    }));
  };

  // Handlers for delivery fee by city
  const handleAddCityFee = () => {
    setSettings(prev => ({
      ...prev,
      deliveryFeeByCity: [...(prev.deliveryFeeByCity || []), { city: "", fee: 0 }]
    }));
  };

  const handleUpdateCityFee = (index, field, value) => {
    setSettings(prev => {
      const updated = [...(prev.deliveryFeeByCity || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, deliveryFeeByCity: updated };
    });
  };

  const handleRemoveCityFee = (index) => {
    setSettings(prev => ({
      ...prev,
      deliveryFeeByCity: (prev.deliveryFeeByCity || []).filter((_, i) => i !== index)
    }));
  };

  // Handlers for category discount defaults
  const handleAddCategoryDiscount = () => {
    setSettings(prev => ({
      ...prev,
      categoryDiscountDefaults: [...(prev.categoryDiscountDefaults || []), { categoryId: null, categoryName: "", maxDiscount: 15 }]
    }));
  };

  const handleUpdateCategoryDiscount = (index, field, value) => {
    setSettings(prev => {
      const updated = [...(prev.categoryDiscountDefaults || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, categoryDiscountDefaults: updated };
    });
  };

  const handleRemoveCategoryDiscount = (index) => {
    setSettings(prev => ({
      ...prev,
      categoryDiscountDefaults: (prev.categoryDiscountDefaults || []).filter((_, i) => i !== index)
    }));
  };

  if (!user || user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  if (loading) {
    return (
      <MainContainer>
        <Paper className={classes.paper} style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress />
        </Paper>
      </MainContainer>
    );
  }

  const hasCredentials = settings.clientId && settings.apiToken;

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <LocalPharmacy style={{ marginRight: 8 }} />
          OtimizAPP API Pharma
        </Title>
      </MainHeader>

      <Paper className={classes.root}>
        {/* Autenticação e Credenciais da API - Seção Unificada */}
        <Card className={classes.card}>
          <CardHeader
            title="Autenticação da API - Credenciais para Integração ERP"
            subheader="Configure e gerencie as credenciais de acesso à API OtimizAPP Pharma"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.apiEnabled}
                      onChange={handleChange("apiEnabled")}
                      color="primary"
                    />
                  }
                  label="Habilitar integração com ERP"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Permite que o ERP integrado consulte e envie dados para o OtimizAPP
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
                  Credenciais de Autenticação OAuth 2.0
                </Typography>
                <Typography className={classes.helperText}>
                  Estas credenciais devem ser configuradas no ERP para autenticação via OAuth 2.0 (Client Credentials).
                  Clique no botão abaixo para gerar automaticamente.
                </Typography>

                <Box style={{ marginTop: 16, marginBottom: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateCredentials}
                    disabled={generatingCredentials || !settings.apiEnabled}
                    startIcon={generatingCredentials ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                  >
                    {hasCredentials ? "Regenerar Credenciais" : "Gerar Credenciais"}
                  </Button>
                </Box>

                {hasCredentials && (
                  <Box className={classes.credentialsBox}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Credenciais {showCredentials ? "visíveis" : "ocultas"}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => setShowCredentials(!showCredentials)}
                          title={showCredentials ? "Ocultar credenciais" : "Mostrar credenciais"}
                        >
                          {showCredentials ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCopyAllCredentials}
                          title="Copiar todas as credenciais"
                        >
                          <FileCopy />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* URL da API */}
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, marginTop: 8 }}>
                      ENDPOINT DA API
                    </Typography>
                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>URL Base:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.apiUrl : "••••••••••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.apiUrl, "URL API")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    {/* OAuth 2.0 */}
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, marginTop: 16, display: "block" }}>
                      AUTENTICAÇÃO OAUTH 2.0 (CLIENT CREDENTIALS)
                    </Typography>
                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>Client ID:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.clientId : "••••••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.clientId, "Client ID")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>Client Secret:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.clientSecret : "••••••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.clientSecret, "Client Secret")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    {/* Credenciais Ecommerce */}
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, marginTop: 16, display: "block" }}>
                      CREDENCIAIS ECOMMERCE (ALTERNATIVO)
                    </Typography>
                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>Usuário:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.ecommerceUser : "••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.ecommerceUser, "Usuário Ecommerce")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>Senha:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.ecommercePassword : "••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.ecommercePassword, "Senha Ecommerce")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    {/* Token de Acesso Direto */}
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, marginTop: 16, display: "block" }}>
                      TOKEN DE ACESSO DIRETO (BEARER)
                    </Typography>
                    <div className={classes.credentialRow}>
                      <span className={classes.credentialLabel}>API Token:</span>
                      <span className={classes.credentialValue}>
                        {showCredentials ? settings.apiToken : "••••••••••••••••••••••••••••••••"}
                      </span>
                      <IconButton
                        size="small"
                        className={classes.copyButton}
                        onClick={() => handleCopyToClipboard(settings.apiToken, "Token de Acesso")}
                      >
                        <FileCopy fontSize="small" />
                      </IconButton>
                    </div>

                    <Typography className={classes.warningText}>
                      ⚠️ Guarde estas credenciais em local seguro. Ao regenerar, as credenciais anteriores serão invalidadas imediatamente.
                    </Typography>

                    {/* Seção de Lojas */}
                    <Box className={classes.storesSection}>
                      <Typography variant="subtitle2" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                        <Store fontSize="small" />
                        Lojas Cadastradas (Store IDs para integração)
                      </Typography>

                      {settings.stores && settings.stores.length > 0 ? (
                        settings.stores.map((store) => (
                          <Box key={store.id} className={classes.storeItem}>
                            <Box className={classes.storeInfo}>
                              <Store color="action" />
                              <Box>
                                <Typography className={classes.storeName}>{store.name}</Typography>
                                <Typography className={classes.storeId}>
                                  Store ID: {showCredentials ? store.erpStoreId : "••••••••••••"}
                                  {store.cnpj && ` | CNPJ: ${store.cnpj}`}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToClipboard(store.erpStoreId, `Store ID ${store.name}`)}
                              title="Copiar Store ID"
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </Box>
                        ))
                      ) : (
                        <Box className={classes.noStoresWarning}>
                          <Warning />
                          <Typography>
                            Nenhuma loja cadastrada. Cadastre lojas em PDV → Lojas para gerar Store IDs de integração.
                          </Typography>
                        </Box>
                      )}

                      <Typography className={classes.helperText} style={{ marginTop: 8 }}>
                        Cada loja possui um Store ID único. Use o Store ID ao fazer requisições para filtrar por loja específica.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!hasCredentials && settings.apiEnabled && (
                  <Typography color="textSecondary" style={{ marginTop: 8 }}>
                    Nenhuma credencial configurada. Clique em "Gerar Credenciais" para criar.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Configurações de Produtos - OtimizAPP API Pharma */}
        <Card className={classes.card}>
          <CardHeader title="Configurações de Produtos - Integração ERP" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendProductPromotions}
                      onChange={handleChange("sendProductPromotions")}
                      color="primary"
                    />
                  }
                  label="Enviar Promoção dos Produtos"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Envia informações de promoções dos produtos para a API
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendPromotionsAndFullMix}
                      onChange={handleChange("sendPromotionsAndFullMix")}
                      color="primary"
                    />
                  }
                  label="Enviar Promoção e Mix Completo dos Produtos"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Envia promoções e o mix completo de produtos
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowControlledProducts}
                      onChange={handleChange("allowControlledProducts")}
                      color="primary"
                    />
                  }
                  label="Permitir Produtos Controlados"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Permite a exibição e venda de medicamentos controlados
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sumAllStores}
                      onChange={handleChange("sumAllStores")}
                      color="primary"
                    />
                  }
                  label="Somar Todas as Lojas"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Soma o estoque de todas as lojas para exibição
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>


        {/* Configurações de Exibição de Produtos */}
        <Card className={classes.card}>
          <CardHeader title="Exibição de Produtos" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showSameFormulaProducts}
                      onChange={handleChange("showSameFormulaProducts")}
                      color="primary"
                    />
                  }
                  label="Exibir produtos com mesma fórmula (princípio ativo)"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Mostra sugestões de medicamentos genéricos e similares com o mesmo princípio ativo
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.suggestRelatedProducts}
                      onChange={handleChange("suggestRelatedProducts")}
                      color="primary"
                    />
                  }
                  label="Sugerir produtos relacionados"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Exibe produtos complementares durante a montagem do pedido
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Configurações do PDV */}
        <Card className={classes.card}>
          <CardHeader title="Configurações do PDV" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableKeyboardShortcuts}
                      onChange={handleChange("enableKeyboardShortcuts")}
                      color="primary"
                    />
                  }
                  label="Habilitar venda rápida por teclado"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Permite usar atalhos de teclado para agilizar operações no PDV
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableQuotes}
                      onChange={handleChange("enableQuotes")}
                      color="primary"
                    />
                  }
                  label="Habilitar orçamentos"
                  className={classes.switchLabel}
                />
                <Typography className={classes.helperText}>
                  Permite criar orçamentos sem compromisso de venda
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Validade do orçamento (dias)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  inputProps={{ min: 1, max: 30 }}
                  value={settings.quoteValidityDays ?? ""}
                  onChange={handleChange("quoteValidityDays")}
                  helperText="Quantos dias o orçamento fica válido (1 a 30 dias)"
                  disabled={!settings.enableQuotes}
                  placeholder="7"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Filtros Padrão do Painel de Vendas */}
        <Card className={classes.card}>
          <CardHeader
            title="Filtros Padrão do Painel de Vendas"
            subheader="Configure os filtros aplicados automaticamente ao buscar produtos"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth size="small">
                  <InputLabel>Filtro de Status</InputLabel>
                  <Select
                    value={settings.salesPanelDefaultActiveFilter ?? "all"}
                    onChange={handleChange("salesPanelDefaultActiveFilter")}
                    label="Filtro de Status"
                  >
                    <MenuItem value="all">Todos os produtos</MenuItem>
                    <MenuItem value="true">Somente ativos</MenuItem>
                    <MenuItem value="false">Somente inativos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth size="small">
                  <InputLabel>Filtro de Estoque</InputLabel>
                  <Select
                    value={settings.salesPanelDefaultStockFilter ?? "all"}
                    onChange={handleChange("salesPanelDefaultStockFilter")}
                    label="Filtro de Estoque"
                  >
                    <MenuItem value="all">Todos os produtos</MenuItem>
                    <MenuItem value="with">Somente com estoque</MenuItem>
                    <MenuItem value="without">Somente sem estoque</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Configurações de Entrega */}
        <Card className={classes.card}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <LocalShipping />
                Configurações de Entrega
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tempo padrão de entrega (minutos)"
                  type="number"
                  value={settings.defaultDeliveryTimeMinutes}
                  onChange={handleChange("defaultDeliveryTimeMinutes")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 1 }}
                />
                <Typography className={classes.helperText}>
                  Tempo estimado padrão para entregas
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Gestão de Localizações e Grupos - CRUD Completo */}
        <Card className={classes.card}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <LocationCity />
                Gestão de Localizações e Grupos de Produtos
              </Box>
            }
            subheader="Cadastre cidades, bairros (com taxas de entrega) e grupos de produtos (com descontos)"
          />
          <CardContent>
            <Tabs
              value={locationTab}
              onChange={(e, v) => setLocationTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label={`Cidades (${cities.length})`} />
              <Tab label={`Bairros (${neighborhoods.length})`} />
              <Tab label="Grupos de Produtos" />
            </Tabs>

            {loadingLocations ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Tab Cidades */}
                {locationTab === 0 && (
                  <Box className={classes.tabPanel}>
                    <Box className={classes.locationHeader}>
                      <Typography variant="subtitle1">
                        Cidades cadastradas com taxa de entrega padrão
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setCityDialog({ open: true, data: null, mode: "create" })}
                      >
                        Nova Cidade
                      </Button>
                    </Box>
                    <Table size="small" className={classes.locationTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Cidade</TableCell>
                          <TableCell>UF</TableCell>
                          <TableCell align="right">Taxa Entrega</TableCell>
                          <TableCell align="right">Tempo (min)</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cities.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              Nenhuma cidade cadastrada. Clique em "Nova Cidade" para adicionar.
                            </TableCell>
                          </TableRow>
                        ) : (
                          cities.map((city) => (
                            <TableRow key={city.id}>
                              <TableCell>{city.name}</TableCell>
                              <TableCell>{city.state}</TableCell>
                              <TableCell align="right">
                                {city.deliveryFee != null ? `R$ ${Number(city.deliveryFee).toFixed(2)}` : "-"}
                              </TableCell>
                              <TableCell align="right">
                                {city.deliveryTime || "-"}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={city.isActive ? "Ativo" : "Inativo"}
                                  size="small"
                                  className={city.isActive ? classes.chipActive : classes.chipInactive}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => setCityDialog({ open: true, data: city, mode: "edit" })}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteCity(city.id)}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Tab Bairros */}
                {locationTab === 1 && (
                  <Box className={classes.tabPanel}>
                    <Box className={classes.locationHeader}>
                      <Typography variant="subtitle1">
                        Bairros com taxas específicas de entrega
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setNeighborhoodDialog({ open: true, data: null, mode: "create" })}
                        disabled={cities.length === 0}
                      >
                        Novo Bairro
                      </Button>
                    </Box>
                    {cities.length === 0 && (
                      <Box className={classes.noStoresWarning} style={{ marginBottom: 16 }}>
                        <Warning />
                        <Typography>Cadastre ao menos uma cidade antes de adicionar bairros.</Typography>
                      </Box>
                    )}
                    <Table size="small" className={classes.locationTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Bairro</TableCell>
                          <TableCell>Cidade</TableCell>
                          <TableCell align="right">Taxa Entrega</TableCell>
                          <TableCell align="right">Tempo (min)</TableCell>
                          <TableCell align="center">Entrega?</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {neighborhoods.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              Nenhum bairro cadastrado.
                            </TableCell>
                          </TableRow>
                        ) : (
                          neighborhoods.map((nb) => (
                            <TableRow key={nb.id}>
                              <TableCell>{nb.name}</TableCell>
                              <TableCell>{nb.city?.name || "-"}</TableCell>
                              <TableCell align="right">
                                {nb.deliveryFee != null ? `R$ ${Number(nb.deliveryFee).toFixed(2)}` : "-"}
                              </TableCell>
                              <TableCell align="right">{nb.deliveryTime || "-"}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={nb.isDeliverable ? "Sim" : "Não"}
                                  size="small"
                                  className={nb.isDeliverable ? classes.chipActive : classes.chipInactive}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => setNeighborhoodDialog({ open: true, data: nb, mode: "edit" })}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteNeighborhood(nb.id)}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Tab Grupos de Produtos */}
                {locationTab === 2 && (
                  <Box className={classes.tabPanel}>
                    <Box className={classes.locationHeader}>
                      <Typography variant="subtitle1">
                        Grupos e subgrupos de produtos (categorias)
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => setGroupDialog({ open: true, data: null, mode: "create" })}
                      >
                        Novo Grupo
                      </Button>
                    </Box>
                    <Table size="small" className={classes.locationTable}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome do Grupo</TableCell>
                          <TableCell>Código</TableCell>
                          <TableCell align="center">Nível</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productGroups.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              Nenhum grupo cadastrado.
                            </TableCell>
                          </TableRow>
                        ) : (
                          flattenGroups(productGroups).map((group) => (
                            <TableRow key={group.id}>
                              <TableCell>
                                <Box style={{ paddingLeft: group.level * 24 }} className={classes.treeItem}>
                                  {group.level > 0 && <span style={{ color: "#999" }}>└─</span>}
                                  <CategoryIcon fontSize="small" color={group.level === 0 ? "primary" : "action"} />
                                  {group.name}
                                </Box>
                              </TableCell>
                              <TableCell>{group.code || "-"}</TableCell>
                              <TableCell align="center">{group.level}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={group.isActive ? "Ativo" : "Inativo"}
                                  size="small"
                                  className={group.isActive ? classes.chipActive : classes.chipInactive}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => setGroupDialog({ open: true, data: group, mode: "edit" })}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Adicionar Subgrupo">
                                  <IconButton
                                    size="small"
                                    onClick={() => setGroupDialog({
                                      open: true,
                                      data: { parentId: group.id, parentName: group.name },
                                      mode: "create"
                                    })}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteGroup(group.id)}
                                    disabled={group.children?.length > 0}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Taxas de Entrega - Configurações Globais */}
        <Card className={classes.card}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <LocalShipping />
                Taxas de Entrega - Configurações Globais
              </Box>
            }
            subheader="Configure taxas base e frete grátis (usadas quando não há bairro/cidade específica)"
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Taxa Base (R$)"
                  type="number"
                  value={settings.deliveryBaseFee ?? ""}
                  onChange={handleChange("deliveryBaseFee")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  placeholder="0"
                />
                <Typography className={classes.helperText}>
                  Valor fixo inicial quando não há taxa de bairro/cidade
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Taxa por Km Rodado (R$)"
                  type="number"
                  value={settings.deliveryFeePerKm ?? ""}
                  onChange={handleChange("deliveryFeePerKm")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  placeholder="0"
                />
                <Typography className={classes.helperText}>
                  Valor adicional por quilômetro
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Frete Grátis Acima de (R$)"
                  type="number"
                  value={settings.deliveryFreeAbove ?? ""}
                  onChange={handleChange("deliveryFreeAbove")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  placeholder="Deixe vazio para desabilitar"
                />
                <Typography className={classes.helperText}>
                  Pedidos acima deste valor têm frete grátis
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box style={{
                  backgroundColor: "#e3f2fd",
                  padding: 16,
                  borderRadius: 8,
                  marginTop: 8
                }}>
                  <Typography variant="body2">
                    <strong>Prioridade de taxas:</strong>
                  </Typography>
                  <Typography variant="body2" component="ol" style={{ marginLeft: 20, marginTop: 4 }}>
                    <li>1º - Taxa do bairro (se cadastrado)</li>
                    <li>2º - Taxa da cidade (se bairro não tem taxa)</li>
                    <li>3º - Taxa base + km (se não tem cadastro)</li>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Descontos por Grupo de Produto */}
        <Card className={classes.card}>
          <CardHeader
            title={
              <Box display="flex" alignItems="center" gap={1}>
                <LocalOffer />
                Descontos por Grupo de Produto
              </Box>
            }
            subheader="Configure limites de desconto para grupos cadastrados acima"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Desconto Mínimo Padrão (%)"
                  type="number"
                  value={settings.defaultMinDiscount ?? 0}
                  onChange={handleChange("defaultMinDiscount")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Desconto Máximo Padrão (%)"
                  type="number"
                  value={settings.defaultMaxDiscount ?? 15}
                  onChange={handleChange("defaultMaxDiscount")}
                  fullWidth
                  variant="outlined"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.helperText}>
                  Os grupos de produtos podem ser gerenciados na aba "Grupos de Produtos" acima.
                  Use a API para definir descontos específicos por grupo.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Mensagens Automáticas */}
        <Card className={classes.card}>
          <CardHeader title="Mensagens Automáticas" />
          <CardContent>
            <Grid container spacing={2}>
              {/* Confirmação de Venda */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendSaleConfirmation}
                      onChange={handleChange("sendSaleConfirmation")}
                      color="primary"
                    />
                  }
                  label="Enviar mensagem após venda"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendQuoteMessage}
                      onChange={handleChange("sendQuoteMessage")}
                      color="primary"
                    />
                  }
                  label="Enviar mensagem após orçamento"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Mensagem de confirmação de venda"
                  value={settings.saleConfirmationMessage || ""}
                  onChange={handleChange("saleConfirmationMessage")}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={6}
                  placeholder={`✅ *Pedido #{numero_pedido}*
{resumo_pedido}
💵 *Total:* {valor_total}
💳 {forma_pagamento}
{mensagem_entrega}
{mensagem_despedida}`}
                  disabled={!settings.sendSaleConfirmation}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Mensagem de orçamento"
                  value={settings.quoteMessage || ""}
                  onChange={handleChange("quoteMessage")}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={6}
                  placeholder={`📋 *Orçamento #{numero_orcamento}*
{resumo_pedido}
💰 *Total:* {valor_total}
⏰ Válido até {data_validade}
Responda *SIM* para confirmar!`}
                  disabled={!settings.sendQuoteMessage}
                />
              </Grid>

              <Grid item xs={12}>
                <Box style={{ backgroundColor: "#f5f5f5", padding: 12, borderRadius: 8 }}>
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Variáveis disponíveis:
                  </Typography>
                  <Typography variant="caption" component="div" style={{ marginTop: 4 }}>
                    <strong>Venda/Orçamento:</strong> {"{numero_pedido}"}, {"{numero_orcamento}"}, {"{resumo_pedido}"}, {"{valor_total}"}, {"{valor_subtotal}"}, {"{valor_desconto}"}, {"{taxa_entrega}"}, {"{forma_pagamento}"}, {"{troco_info}"}
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>Cliente:</strong> {"{nome_cliente}"}, {"{telefone_cliente}"}, {"{endereco_cliente}"}, {"{bairro_cliente}"}, {"{cidade_cliente}"}
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>Datas:</strong> {"{data_pedido}"}, {"{hora_pedido}"}, {"{data_validade}"}, {"{validade_dias}"}
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>Atendimento:</strong> {"{nome_atendente}"}, {"{nome_loja}"}, {"{telefone_loja}"}
                  </Typography>
                  <Typography variant="caption" component="div">
                    <strong>Entrega:</strong> {"{tempo_entrega}"}, {"{mensagem_entrega}"}, {"{mensagem_despedida}"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Mensagem sobre entrega"
                  value={settings.deliveryMessage || ""}
                  onChange={handleChange("deliveryMessage")}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  placeholder="🚚 Entrega em *{tempo_entrega}*"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Mensagem de despedida"
                  value={settings.saleFarewellMessage || ""}
                  onChange={handleChange("saleFarewellMessage")}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  placeholder="Obrigado pela preferência! 🙏"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? "Salvando..." : "Salvar Configurações"}
          </Button>
          <Button
            variant="outlined"
            onClick={loadSettings}
            disabled={loading}
          >
            Cancelar
          </Button>
        </div>
      </Paper>

      {/* Dialog de Cidade */}
      <CityDialog
        open={cityDialog.open}
        data={cityDialog.data}
        mode={cityDialog.mode}
        onClose={() => setCityDialog({ open: false, data: null, mode: "create" })}
        onSave={handleSaveCity}
      />

      {/* Dialog de Bairro */}
      <NeighborhoodDialog
        open={neighborhoodDialog.open}
        data={neighborhoodDialog.data}
        mode={neighborhoodDialog.mode}
        cities={cities}
        onClose={() => setNeighborhoodDialog({ open: false, data: null, mode: "create" })}
        onSave={handleSaveNeighborhood}
      />

      {/* Dialog de Grupo */}
      <GroupDialog
        open={groupDialog.open}
        data={groupDialog.data}
        mode={groupDialog.mode}
        groups={productGroups}
        onClose={() => setGroupDialog({ open: false, data: null, mode: "create" })}
        onSave={handleSaveGroup}
      />
    </MainContainer>
  );
};

// Dialog para Cidade
const CityDialog = ({ open, data, mode, onClose, onSave }) => {
  const [form, setForm] = useState({ name: "", state: "", deliveryFee: "", deliveryTime: "", isActive: true });

  useEffect(() => {
    if (data && mode === "edit") {
      setForm({
        name: data.name || "",
        state: data.state || "",
        deliveryFee: data.deliveryFee ?? "",
        deliveryTime: data.deliveryTime ?? "",
        isActive: data.isActive !== false,
      });
    } else {
      setForm({ name: "", state: "", deliveryFee: "", deliveryTime: "", isActive: true });
    }
  }, [data, mode, open]);

  const handleSubmit = () => {
    onSave({
      name: form.name,
      state: form.state.toUpperCase(),
      deliveryFee: form.deliveryFee === "" ? null : parseFloat(form.deliveryFee),
      deliveryTime: form.deliveryTime === "" ? null : parseInt(form.deliveryTime),
      isActive: form.isActive,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "edit" ? "Editar Cidade" : "Nova Cidade"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={8}>
            <TextField
              label="Nome da Cidade"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="UF"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 2 }}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Taxa de Entrega (R$)"
              type="number"
              value={form.deliveryFee}
              onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Tempo de Entrega (min)"
              type="number"
              value={form.deliveryTime}
              onChange={(e) => setForm({ ...form, deliveryTime: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Cidade ativa"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!form.name || !form.state}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog para Bairro
const NeighborhoodDialog = ({ open, data, mode, cities, onClose, onSave }) => {
  const [form, setForm] = useState({
    cityId: "", name: "", deliveryFee: "", deliveryTime: "", isDeliverable: true, isActive: true
  });

  useEffect(() => {
    if (data && mode === "edit") {
      setForm({
        cityId: data.cityId || "",
        name: data.name || "",
        deliveryFee: data.deliveryFee ?? "",
        deliveryTime: data.deliveryTime ?? "",
        isDeliverable: data.isDeliverable !== false,
        isActive: data.isActive !== false,
      });
    } else {
      setForm({ cityId: cities[0]?.id || "", name: "", deliveryFee: "", deliveryTime: "", isDeliverable: true, isActive: true });
    }
  }, [data, mode, open, cities]);

  const handleSubmit = () => {
    onSave({
      cityId: form.cityId,
      name: form.name,
      deliveryFee: form.deliveryFee === "" ? null : parseFloat(form.deliveryFee),
      deliveryTime: form.deliveryTime === "" ? null : parseInt(form.deliveryTime),
      isDeliverable: form.isDeliverable,
      isActive: form.isActive,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "edit" ? "Editar Bairro" : "Novo Bairro"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth size="small" required>
              <InputLabel>Cidade</InputLabel>
              <Select
                value={form.cityId}
                onChange={(e) => setForm({ ...form, cityId: e.target.value })}
                label="Cidade"
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>{city.name} - {city.state}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nome do Bairro"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Taxa de Entrega (R$)"
              type="number"
              value={form.deliveryFee}
              onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Tempo de Entrega (min)"
              type="number"
              value={form.deliveryTime}
              onChange={(e) => setForm({ ...form, deliveryTime: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isDeliverable}
                  onChange={(e) => setForm({ ...form, isDeliverable: e.target.checked })}
                  color="primary"
                />
              }
              label="Faz entrega"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Ativo"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!form.name || !form.cityId}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog para Grupo de Produto
const GroupDialog = ({ open, data, mode, groups, onClose, onSave }) => {
  const [form, setForm] = useState({ name: "", code: "", parentId: "", description: "", isActive: true });

  // Flatten groups for parent select
  const flattenForSelect = (grps, level = 0) => {
    let result = [];
    for (const g of grps) {
      result.push({ id: g.id, name: g.name, level });
      if (g.children?.length > 0) {
        result = result.concat(flattenForSelect(g.children, level + 1));
      }
    }
    return result;
  };

  useEffect(() => {
    if (mode === "edit" && data) {
      setForm({
        name: data.name || "",
        code: data.code || "",
        parentId: data.parentId || "",
        description: data.description || "",
        isActive: data.isActive !== false,
      });
    } else if (mode === "create" && data?.parentId) {
      // Creating subgroup
      setForm({ name: "", code: "", parentId: data.parentId, description: "", isActive: true });
    } else {
      setForm({ name: "", code: "", parentId: "", description: "", isActive: true });
    }
  }, [data, mode, open]);

  const handleSubmit = () => {
    onSave({
      name: form.name,
      code: form.code || null,
      parentId: form.parentId || null,
      description: form.description || null,
      isActive: form.isActive,
    });
  };

  const flatGroups = flattenForSelect(groups);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "edit" ? "Editar Grupo" : data?.parentId ? `Novo Subgrupo de "${data.parentName}"` : "Novo Grupo"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={8}>
            <TextField
              label="Nome do Grupo"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Código"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          {!data?.parentId && mode !== "edit" && (
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel>Grupo Pai (opcional)</InputLabel>
                <Select
                  value={form.parentId}
                  onChange={(e) => setForm({ ...form, parentId: e.target.value })}
                  label="Grupo Pai (opcional)"
                >
                  <MenuItem value="">Nenhum (grupo raiz)</MenuItem>
                  {flatGroups.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {"─".repeat(g.level)} {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Descrição"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Grupo ativo"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!form.name}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PharmaSettings;
